import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';

/**
 * The browser tab title: the page's title (from the route, in the active language — TranslatedTitleStrategy) with the
 * unread count in front, e.g. "(56) Accueil · Crewdle". The single writer of `document.title` for routed pages: the
 * count used to overwrite the route title with "(56) Crewdle AI" (staging 2026-11-05, Home in French).
 */
@Injectable({
  providedIn: 'root'
})
export class TabNotificationService implements OnDestroy {
  private readonly title = inject(Title);
  private readonly ngZone = inject(NgZone);
  private readonly translate = inject(TranslateService, { optional: true });

  private flashInterval: ReturnType<typeof setInterval> | null = null;
  private showingPreview = false;
  private unreadCount = 0;
  private pageTitle: string | null = null;
  private visibilityHandler = () => this.onVisibilityChange();

  public static readonly BASE_TITLE = 'Crewdle AI';
  public static readonly BRAND = 'Crewdle';
  private static readonly FLASH_INTERVAL_MS = 2000;

  constructor() {
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  ngOnDestroy(): void {
    this.stopFlashing();
    document.removeEventListener('visibilitychange', this.visibilityHandler);
  }

  /** The current page's title, already translated (null: no page title — the tab reads "Crewdle AI"). */
  public setPageTitle(pageTitle: string | null): void {
    const t = (pageTitle ?? '').trim();
    // A route titled after the brand itself ("Crewdle") reads as the base title, not "Crewdle · Crewdle".
    this.pageTitle = t && t !== TabNotificationService.BRAND && t !== TabNotificationService.BASE_TITLE ? t : null;
    if (!this.showingPreview) this.title.setTitle(this.buildCountTitle());
  }

  public update(unreadCount: number): void {
    this.unreadCount = unreadCount;

    if (unreadCount === 0) {
      this.stopFlashing();
      this.title.setTitle(this.buildCountTitle());
      return;
    }

    this.title.setTitle(this.buildCountTitle());

    if (document.visibilityState === 'hidden') {
      this.startFlashing();
    } else {
      this.stopFlashing();
    }
  }

  // --- Tab title flashing ---

  private startFlashing(): void {
    if (this.flashInterval) {
      return;
    }

    this.showingPreview = false;
    this.ngZone.runOutsideAngular(() => {
      this.flashInterval = setInterval(() => {
        this.showingPreview = !this.showingPreview;

        const text = this.showingPreview
          ? this.unreadPreview()
          : this.buildCountTitle();

        this.ngZone.run(() => this.title.setTitle(text));
      }, TabNotificationService.FLASH_INTERVAL_MS);
    });
  }

  private stopFlashing(): void {
    if (this.flashInterval) {
      clearInterval(this.flashInterval);
      this.flashInterval = null;
    }
    this.showingPreview = false;
  }

  private onVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      this.stopFlashing();
      const text = this.buildCountTitle();
      this.ngZone.run(() => this.title.setTitle(text));
    } else if (this.unreadCount > 0) {
      this.startFlashing();
    }
  }

  private unreadPreview(): string {
    const key = 'tab-title.unread-messages';
    const text = this.translate?.instant(key);
    return text && text !== key ? text : 'Unread messages';
  }

  /** "(56) Accueil · Crewdle", "Accueil · Crewdle", "(3) Crewdle AI" or "Crewdle AI". */
  private buildCountTitle(): string {
    const base = this.pageTitle ? `${this.pageTitle} · ${TabNotificationService.BRAND}` : TabNotificationService.BASE_TITLE;
    if (this.unreadCount <= 0) return base;
    const display = this.unreadCount > 99 ? '99+' : this.unreadCount;
    return `(${display}) ${base}`;
  }

}
